import { Skeleton } from '@mui/material';
import {
  formatDateLocal,
  formatPercent,
  format as generalFormatter,
  type ValueFormat,
} from '@poweralpha/common';
import { get } from 'lodash';
import { formatCount, formatDollar, formatInt, formatMultiplier, formatRound } from '../utils';
import Checkmark from './Checkmark';

export type Value = number | string | Date | null | boolean | undefined;

interface BaseFormatValueProps<T extends Value> {
  value: T;
  isLoading?: boolean;
  width?: number;
  format?: ValueFormat;
}

interface NumberFormatProps {
  precision?: number;
  scale?: number;
  disableMagnitudeChar?: boolean;
}

export type FormatValueProps<T extends Value> = T extends string | Date | null | undefined
  ? BaseFormatValueProps<T | undefined | null> & Partial<Record<keyof NumberFormatProps, never>>
  : T extends number
    ? BaseFormatValueProps<T | undefined | null> & NumberFormatProps
    : BaseFormatValueProps<T> & Partial<Record<keyof NumberFormatProps, never>>;

export function FormatValue<T extends Value>({
  isLoading,
  value,
  format,
  width,
  ...props
}: FormatValueProps<T>) {
  if (isLoading) {
    return <Skeleton width={width} />;
  }
  if (value instanceof Date || (typeof value === 'string' && format === 'date')) {
    return <span>{formatDateLocal(value, true)}</span>;
  }
  if (typeof value === 'string') {
    return <span>{value}</span>;
  }
  if (typeof value === 'boolean') {
    return <Checkmark checked={value} sx={{ height: 17, width: 17 }} />;
  }
  const precision = get(props, 'precision') as number | undefined;
  const scale = get(props, 'scale') as number | undefined;
  const disableMagnitudeChar = get(props, 'disableMagnitudeChar') as boolean | undefined;
  if (format === 'percent') {
    return (
      <span>{formatPercent(value, get(props, 'precision') as number | undefined, scale)}</span>
    );
  }
  if (format === 'dollar') {
    return <span>{formatDollar(value, precision, scale, disableMagnitudeChar)}</span>;
  }
  if (format === 'multiplier') {
    return <span>{formatMultiplier(value)}</span>;
  }
  if (format === 'int') {
    return <span>{formatInt(value)}</span>;
  }
  if (format === 'count') {
    return <span>{formatCount(value)}</span>;
  }
  if (precision !== undefined) {
    return <span>{formatRound(value, precision, scale)}</span>;
  }
  return <span>{generalFormatter(value, precision, scale)?.toString() ?? ''}</span>;
}
